import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import dataService from '../../services/dataService';
import '../../features/brand/cwm-tokens.css';

/**
 * UnifiedPreferences — dashboard-embedded taste-preference editor.
 *
 * Mirrors UnifiedAllergies in structure but with green "personal taste" tone
 * instead of red "safety" tone. Five preference groups (Diet / Cuisine /
 * Spice / Texture / Avoid) + like/love slider per entry.
 *
 * Data shape stays compatible: { ingredient, level } where level is -1 to 1.
 *   -1 = strong dislike, 0 = neutral, +1 = strong love.
 */

const EASE = [0.22, 1, 0.36, 1];

const QUICK_PICKS = [
  { group: 'Diet',    picks: [
    { label: 'Vegetarian', level: 0.8 },
    { label: 'Vegan',      level: 0.8 },
    { label: 'Keto',       level: 0.8 },
    { label: 'Paleo',      level: 0.8 },
    { label: 'Mediterranean', level: 0.8 },
  ]},
  { group: 'Cuisine', picks: [
    { label: 'Italian',   level: 0.7 },
    { label: 'Japanese',  level: 0.7 },
    { label: 'Mexican',   level: 0.7 },
    { label: 'Thai',      level: 0.7 },
    { label: 'Indian',    level: 0.7 },
    { label: 'French',    level: 0.7 },
  ]},
  { group: 'Preferences', picks: [
    { label: 'Spicy food',  level: 0.6 },
    { label: 'Mild spice',  level: 0.4 },
    { label: 'Sweet',       level: 0.5 },
    { label: 'Savory',      level: 0.7 },
    { label: 'Low sodium',  level: 0.5 },
    { label: 'High protein', level: 0.5 },
  ]},
];

const LEVELS = [
  { id: 'avoid',   label: 'Avoid',    level: -0.8 },
  { id: 'dislike', label: 'Dislike',  level: -0.4 },
  { id: 'neutral', label: 'Neutral',  level: 0 },
  { id: 'like',    label: 'Like',     level: 0.5 },
  { id: 'love',    label: 'Love',     level: 0.9 },
];

function levelToId(level) {
  if (level == null) return 'neutral';
  if (level >= 0.65) return 'love';
  if (level >= 0.2) return 'like';
  if (level >= -0.2) return 'neutral';
  if (level >= -0.6) return 'dislike';
  return 'avoid';
}

const LEVEL_TONES = {
  avoid:   { bg: 'var(--cwm-red-light)',   fg: 'var(--cwm-red-text)',   solid: '#B91C1C' },
  dislike: { bg: '#FFEEDD',                fg: '#8A4A00',               solid: '#EA580C' },
  neutral: { bg: 'var(--cwm-gray-100)',    fg: 'var(--cwm-gray-700)',   solid: 'var(--cwm-gray-500)' },
  like:    { bg: 'var(--cwm-blue-pale)',   fg: 'var(--cwm-blue-deep)',  solid: 'var(--cwm-blue-deep)' },
  love:    { bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)', solid: 'var(--cwm-green)' },
};

export default function UnifiedPreferences({ userId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);
  const [customInput, setCustomInput] = useState('');

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const data = await dataService.loadUserPreferences(userId, 'preference');
        if (cancelled) return;
        const normalized = (Array.isArray(data) ? data : []).map((x) => ({
          ingredient: x.ingredient || x.name || String(x),
          level: typeof x.level === 'number' ? x.level : 0.5,
        }));
        setItems(normalized);
      } catch { if (!cancelled) setItems([]); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const has = (name) => items.some((i) => i.ingredient.toLowerCase() === name.toLowerCase());

  const add = (name, level = 0.5) => {
    if (!name.trim()) return;
    if (has(name)) { flash('red', `${name} is already in your list.`); return; }
    setItems((prev) => [...prev, { ingredient: name.trim(), level }]);
  };

  const updateLevel = (ingredient, levelId) => {
    const level = LEVELS.find((l) => l.id === levelId)?.level ?? 0;
    setItems((prev) => prev.map((i) => i.ingredient === ingredient ? { ...i, level } : i));
  };

  const remove = (ingredient) => setItems((prev) => prev.filter((i) => i.ingredient !== ingredient));

  const save = async () => {
    if (!userId) { flash('red', 'Sign in required.'); return; }
    setSaving(true);
    try {
      await dataService.saveUserPreferences(userId, items, 'preference');
      flash('green', 'Preferences saved. Chat will use these on every menu.');
    } catch { flash('red', 'Failed to save. Try again.'); }
    finally { setSaving(false); }
  };

  const flash = (tone, text) => {
    setAlert({ tone, text, id: Date.now() });
    setTimeout(() => setAlert(null), 3000);
  };

  return (
    <div style={{ padding: '18px 20px' }}>

      <AnimatePresence>
        {alert && (
          <motion.div
            key={alert.id}
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            style={{
              padding: '10px 14px', borderRadius: 14, marginBottom: 14,
              background: alert.tone === 'red' ? 'var(--cwm-red-light)' : 'var(--cwm-green-light)',
              color: alert.tone === 'red' ? 'var(--cwm-red-text)' : 'var(--cwm-green-text)',
              fontSize: 13, fontWeight: 800,
            }}
          >{alert.text}</motion.div>
        )}
      </AnimatePresence>

      {/* Intro callout */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.46, ease: EASE }}
        style={{
          padding: '18px 20px',
          borderRadius: 20,
          background: 'linear-gradient(160deg, #E6FBEF 0%, #C5F0D4 100%)',
          border: '1.5px solid #A6E8C2',
          marginBottom: 22,
          display: 'flex', alignItems: 'center', gap: 14,
        }}
      >
        <div style={{
          width: 48, height: 48, borderRadius: 14,
          background: '#fff', color: 'var(--cwm-green-text)',
          display: 'grid', placeItems: 'center', flexShrink: 0,
          boxShadow: '0 6px 14px rgba(22,128,60,0.18)',
        }}>
          <HeartIcon size={22} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="cwm-eyebrow" style={{ color: 'var(--cwm-green-text)' }}>PERSONAL TASTE</div>
          <div style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--cwm-ink)', marginTop: 2, lineHeight: 1.4 }}>
            {items.length === 0
              ? 'Tell us what you like and avoid so recommendations actually sound like you.'
              : `Tracking ${items.length} taste signal${items.length === 1 ? '' : 's'}.`}
          </div>
        </div>
      </motion.div>

      {/* Quick picks grouped */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.46, delay: 0.08, ease: EASE }}
        style={{ marginBottom: 20 }}
      >
        <div className="cwm-eyebrow" style={{ marginBottom: 12 }}>QUICK ADD</div>
        <div style={{ display: 'grid', gap: 16 }}>
          {QUICK_PICKS.map((g) => (
            <div key={g.group}>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: 'var(--cwm-gray-500)', marginBottom: 6, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {g.group}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {g.picks.map((p, i) => {
                  const already = has(p.label);
                  return (
                    <motion.button
                      key={p.label} type="button"
                      onClick={() => !already && add(p.label, p.level)}
                      disabled={already}
                      whileHover={!already ? { y: -2 } : {}} whileTap={!already ? { scale: 0.96 } : {}}
                      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.28, delay: i * 0.02 }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '7px 14px', borderRadius: 999,
                        background: already ? 'var(--cwm-gray-100)' : '#fff',
                        color: already ? 'var(--cwm-gray-400)' : 'var(--cwm-green-text)',
                        border: `1.5px solid ${already ? 'var(--cwm-gray-200)' : '#A6E8C2'}`,
                        fontFamily: 'var(--cwm-font)', fontSize: 12.5, fontWeight: 800,
                        cursor: already ? 'not-allowed' : 'pointer',
                        transition: 'all 140ms ease',
                      }}>
                      {already ? <CheckIcon size={11} /> : <PlusIcon size={11} />} {p.label}
                    </motion.button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Custom add */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.46, delay: 0.16, ease: EASE }}
        style={{ marginBottom: 24, display: 'flex', gap: 8 }}
      >
        <input
          value={customInput}
          onChange={(e) => setCustomInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(customInput); setCustomInput(''); } }}
          placeholder="Add your own — e.g. 'cilantro', 'char on grilled food', 'no fennel'…"
          style={{
            flex: 1, minWidth: 0,
            padding: '12px 16px', borderRadius: 14,
            border: '1.5px solid #A6E8C2',
            background: 'var(--cwm-green-light)',
            color: 'var(--cwm-ink)',
            fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 600,
            outline: 'none',
          }}
        />
        <button
          type="button"
          onClick={() => { add(customInput); setCustomInput(''); }}
          disabled={!customInput.trim()}
          style={{
            padding: '12px 18px', borderRadius: 14,
            background: customInput.trim() ? 'var(--cwm-green)' : 'var(--cwm-gray-100)',
            color: customInput.trim() ? '#fff' : 'var(--cwm-gray-500)',
            border: 'none',
            fontFamily: 'var(--cwm-font)', fontSize: 13.5, fontWeight: 900,
            cursor: customInput.trim() ? 'pointer' : 'not-allowed',
            display: 'inline-flex', alignItems: 'center', gap: 6,
            boxShadow: customInput.trim() ? '0 4px 12px rgba(22,128,60,0.28)' : 'none',
          }}>
          <PlusIcon size={14} /> Add
        </button>
      </motion.div>

      {/* List */}
      {loading ? (
        <LoadingBlock />
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <div style={{ marginBottom: 24 }}>
          <div className="cwm-eyebrow" style={{ marginBottom: 10 }}>YOUR PREFERENCES ({items.length})</div>
          <div style={{ display: 'grid', gap: 10 }}>
            <AnimatePresence initial={false}>
              {items.map((item, i) => (
                <PreferenceRow
                  key={item.ingredient}
                  item={item}
                  index={i}
                  onLevelChange={(id) => updateLevel(item.ingredient, id)}
                  onRemove={() => remove(item.ingredient)}
                />
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Save */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, paddingTop: 14, borderTop: '1px solid var(--cwm-gray-100)' }}>
        <div style={{ flex: 1, fontSize: 12.5, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>
          Chat uses these to rank menu picks by what you'll actually enjoy.
        </div>
        <button
          type="button" onClick={save} disabled={saving}
          className="cwm-btn"
          style={{ opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? <Dots /> : <><SaveIcon size={14} /> Save preferences</>}
        </button>
      </div>
    </div>
  );
}

function PreferenceRow({ item, index, onLevelChange, onRemove }) {
  const levelId = levelToId(item.level);
  const tone = LEVEL_TONES[levelId];
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 40, scale: 0.95 }}
      transition={{ duration: 0.28, delay: index * 0.03, ease: EASE }}
      className="cwm-sheet"
      style={{ padding: '14px 16px' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 12,
            background: tone.bg, color: tone.fg,
            display: 'grid', placeItems: 'center', flexShrink: 0,
            border: `1.5px solid ${tone.solid}33`,
          }}>
            <HeartIcon size={16} />
          </div>
          <div style={{ fontSize: 15.5, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.005em' }}>
            {item.ingredient}
          </div>
        </div>
        <button
          type="button" onClick={onRemove} aria-label="remove"
          style={{
            width: 30, height: 30, borderRadius: 999,
            background: '#fff', color: 'var(--cwm-gray-500)',
            border: '1px solid var(--cwm-gray-200)',
            cursor: 'pointer', display: 'grid', placeItems: 'center', flexShrink: 0,
          }}>
          <TrashIcon size={13} />
        </button>
      </div>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
        {LEVELS.map((l) => {
          const on = l.id === levelId;
          const st = LEVEL_TONES[l.id];
          return (
            <button
              key={l.id} type="button"
              onClick={() => onLevelChange(l.id)}
              style={{
                padding: '6px 12px', borderRadius: 999,
                background: on ? st.solid : st.bg,
                color: on ? '#fff' : st.fg,
                border: `1.5px solid ${on ? st.solid : 'transparent'}`,
                fontFamily: 'var(--cwm-font)', fontSize: 11.5, fontWeight: 900,
                cursor: 'pointer',
                boxShadow: on ? `0 3px 8px ${st.solid}55` : 'none',
                transition: 'all 140ms ease',
              }}>
              {l.label}
            </button>
          );
        })}
      </div>
    </motion.div>
  );
}

function EmptyState() {
  return (
    <div style={{
      padding: '36px 24px', textAlign: 'center',
      borderRadius: 20,
      background: 'var(--cwm-gray-50)',
      border: '1.5px dashed var(--cwm-gray-300)',
      marginBottom: 24,
    }}>
      <div style={{
        width: 56, height: 56, margin: '0 auto 12px',
        borderRadius: '50%',
        background: '#fff', color: 'var(--cwm-gray-500)',
        display: 'grid', placeItems: 'center',
        border: '1.5px dashed var(--cwm-gray-300)',
      }}>
        <HeartIcon size={24} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', marginBottom: 4 }}>
        No preferences yet
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--cwm-gray-500)', maxWidth: 360, margin: '0 auto' }}>
        Tap a quick pick above or type your own taste. Chat will rank menus by what you'll love.
      </div>
    </div>
  );
}

function LoadingBlock() {
  return (
    <div style={{ padding: '30px 20px', textAlign: 'center', color: 'var(--cwm-gray-500)', fontSize: 13, fontWeight: 700 }}>
      <div style={{ width: 32, height: 32, margin: '0 auto 10px', border: '3px solid var(--cwm-green-light)', borderTopColor: 'var(--cwm-green)', borderRadius: '50%', animation: 'cwmSpin 900ms linear infinite' }}/>
      <style>{`@keyframes cwmSpin { to { transform: rotate(360deg); } }`}</style>
      Loading your preferences…
    </div>
  );
}

function Dots() {
  return (
    <span style={{ display: 'inline-flex', gap: 4 }}>
      {[0, 1, 2].map((i) => <span key={i} style={{ width: 6, height: 6, borderRadius: 999, background: '#fff', animation: 'cwmDot 900ms ease-in-out infinite', animationDelay: `${i * 150}ms` }}/>)}
    </span>
  );
}

function HeartIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 21s-7-5.5-7-11a4 4 0 017-2.6A4 4 0 0119 10c0 5.5-7 11-7 11z"/></svg>; }
function PlusIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>; }
function CheckIcon({ size = 11 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg>; }
function TrashIcon({ size = 13 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>; }
function SaveIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2zM17 21v-8H7v8M7 3v5h8"/></svg>; }
